import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  NgZone,
} from "@angular/core";
import { SharedModule } from "../../shared/shared.module";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ActiveCallInfo } from "../../shared/services/sip/sip.service";
import { TranslateService } from "@ngx-translate/core";
import { MatTooltip } from "@angular/material/tooltip";
import { VideoStats } from "../../shared/services/video/video.interface";
import {
  VideoQualityService,
  MAX_VIDEO_BITRATE,
} from "../../shared/services/video/video-quality.service";

export type VideoLayout = "pip" | "fullscreen" | "expanded";
export type ConnectionQuality = "excellent" | "good" | "fair" | "poor";

@Component({
  selector: "app-video-display",
  standalone: true,
  imports: [SharedModule],
  templateUrl: "./video-display.component.html",
  styleUrls: ["./video-display.component.scss"],
})
export class VideoDisplayComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() call: ActiveCallInfo | null = null;
  @Input() layout: VideoLayout = "pip";
  @Input() showControls: boolean = true;
  @Input() controlsVisible: boolean = true;
  @Input() callDuration: string = "00:00:00";
  @Input() dtmfKey: { key: string; id: number } | null = null;

  @Output() fullscreenToggle = new EventEmitter<void>();
  @Output() undockRequest = new EventEmitter<void>();
  @Output() closeVideo = new EventEmitter<void>();
  @Output() dtmfInput = new EventEmitter<string>();

  @ViewChild("remoteVideo") remoteVideoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild("localVideo") localVideoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild("dtmfHiddenInput") dtmfHiddenInput:
    | ElementRef<HTMLInputElement>
    | undefined;
  @ViewChild("statsTooltip") statsTooltip: MatTooltip | undefined;

  private destroyed$ = new Subject<void>();

  // UI state
  isRemoteVideoLoading = true;
  connectionQuality: ConnectionQuality = "good";
  showConnectionIndicator = true;
  isLocalVideoPinned = true;

  // Video stats for display
  currentVideoStats: VideoStats | null = null;
  private statsSubscription: Subscription | null = null;
  private statsTooltipVisible = false;

  // DTMF visual feedback
  showDtmfOverlay: boolean = false;
  dtmfDisplayKey: string = "";

  // Valid DTMF characters for mobile input filtering
  private static readonly VALID_DTMF = new Set([
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "*",
    "#",
  ]);

  // Track video track IDs to detect when tracks change within the same stream
  private lastLocalVideoTrackId: string | null = null;
  private lastRemoteVideoTrackId: string | null = null;

  constructor(
    private translate: TranslateService,
    private videoQualityService: VideoQualityService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Initialize
  }

  ngAfterViewInit(): void {
    // Set up video elements when call data is available
    if (this.call) {
      this.setupVideoStreams();
      // Trigger change detection after modifying state in ngAfterViewInit
      // This prevents ExpressionChangedAfterItHasBeenCheckedError
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.cleanupVideoElements();
    this.stopStatsSubscription();
  }

  /**
   * Set up video element srcObject bindings
   */
  private setupVideoStreams(): void {
    if (!this.call) return;

    // If remote party is not sending video, don't show loading state
    // This happens when callee accepts with audio-only
    if (!this.call.hasRemoteVideo) {
      this.isRemoteVideoLoading = false;
    }

    // Set remote video stream
    if (this.call.remoteStream && this.remoteVideoElement) {
      const remoteVideo = this.remoteVideoElement.nativeElement;
      const srcObjectChanged = remoteVideo.srcObject !== this.call.remoteStream;
      const hasVideoTracks = this.call.remoteStream.getVideoTracks().length > 0;

      // Update srcObject if it has changed (prevents flickering)
      if (srcObjectChanged) {
        remoteVideo.srcObject = this.call.remoteStream;

        remoteVideo.play().catch((error) => {
          console.error("Error playing remote video:", error);
        });
      }

      // Set up loadedmetadata handler when:
      // 1. srcObject changed (new stream), OR
      // 2. Video tracks exist and we're still in loading state (video upgrade scenario)
      // The second case handles when video tracks are added to an existing stream
      // (same srcObject reference, but now has video from remote upgrade)
      if (srcObjectChanged || (hasVideoTracks && this.isRemoteVideoLoading)) {
        // Handle video loaded
        remoteVideo.onloadedmetadata = () => {
          this.isRemoteVideoLoading = false;
          this.cdr.detectChanges();
        };

        // Handle errors
        remoteVideo.onerror = (error) => {
          console.error("Remote video error:", error);
          this.isRemoteVideoLoading = false;
          this.cdr.detectChanges();
        };
      }

      // Always check if metadata is already available
      // readyState >= 1 means HAVE_METADATA or higher
      // This handles cases where video is ready before our handlers are set
      if (remoteVideo.readyState >= 1 && hasVideoTracks) {
        this.isRemoteVideoLoading = false;
      }
    }

    // Set local video stream
    if (this.localVideoElement) {
      const localVideo = this.localVideoElement.nativeElement;

      if (this.call.localStream) {
        // Check if video track has changed (even if stream reference is the same)
        // This handles the case where a new camera track replaces a dormant black track
        const currentVideoTrack = this.call.localStream.getVideoTracks()[0];
        const currentVideoTrackId = currentVideoTrack?.id || null;
        const videoTrackChanged =
          currentVideoTrackId !== this.lastLocalVideoTrackId;
        const streamChanged = localVideo.srcObject !== this.call.localStream;

        // Update srcObject if stream changed OR video track changed
        if (streamChanged || videoTrackChanged) {
          // Force refresh by setting srcObject again (even if same stream)
          // This ensures the video element picks up the new video track
          if (videoTrackChanged && !streamChanged) {
            console.log(
              `[VideoDisplay] Local video track changed: ${this.lastLocalVideoTrackId} -> ${currentVideoTrackId}`,
            );
            // Setting srcObject = null then back forces the element to re-evaluate tracks
            localVideo.srcObject = null;
          }

          localVideo.srcObject = this.call.localStream;
          this.lastLocalVideoTrackId = currentVideoTrackId;

          // Start playback only when setting new stream
          localVideo.play().catch((error) => {
            console.error("Error playing local video:", error);
          });

          // Handle errors
          localVideo.onerror = (error) => {
            console.error("Local video error:", error);
          };
        }
        // If stream and track are the same, no action needed
      } else {
        localVideo.srcObject = null;
        this.lastLocalVideoTrackId = null;
      }
    }
  }

  /**
   * Start subscribing to video stats (called on indicator hover)
   */
  startStatsSubscription(): void {
    if (this.statsSubscription || !this.call?.id) return;

    this.statsSubscription = this.videoQualityService
      .getVideoStats(this.call.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((stats) => {
        if (stats) {
          this.updateVideoStats(stats);
        }
      });
  }

  /**
   * Stop subscribing to video stats (called on indicator mouse leave)
   */
  stopStatsSubscription(): void {
    if (this.statsSubscription) {
      this.statsSubscription.unsubscribe();
      this.statsSubscription = null;
    }
  }

  toggleStatsTooltip(event: Event): void {
    event.preventDefault();
    this.statsTooltipVisible = !this.statsTooltipVisible;
    if (this.statsTooltipVisible) {
      this.startStatsSubscription();
      this.statsTooltip?.show();
    } else {
      this.statsTooltip?.hide();
      this.stopStatsSubscription();
    }
  }

  /**
   * Update video statistics for display
   */
  private updateVideoStats(stats: VideoStats): void {
    this.currentVideoStats = stats;

    // Determine connection quality based on inbound stats (what we receive)
    // Bitrate thresholds are proportional to MAX_VIDEO_BITRATE (in kbps)
    const maxBitrateKbps = MAX_VIDEO_BITRATE / 1000;
    const excellentThreshold = maxBitrateKbps * 0.5; // 50% of max = excellent
    const goodThreshold = maxBitrateKbps * 0.25; // 25% of max = good

    const inbound = stats.inbound;
    const packetsLost = inbound?.packetsLost || 0;
    const packetsTotal = inbound?.packetsTotal || 1;
    const packetLossRate = packetsTotal > 0 ? packetsLost / packetsTotal : 0;
    const inboundBitrate = inbound ? Math.round(inbound.bitrate / 1000) : 0;

    if (packetLossRate < 0.02 && inboundBitrate > excellentThreshold) {
      this.connectionQuality = "excellent";
    } else if (packetLossRate < 0.05 && inboundBitrate > goodThreshold) {
      this.connectionQuality = "good";
    } else if (packetLossRate < 0.1) {
      this.connectionQuality = "fair";
    } else {
      this.connectionQuality = "poor";
    }
  }

  /**
   * Clean up video elements on component destroy
   */
  private cleanupVideoElements(): void {
    if (this.remoteVideoElement?.nativeElement) {
      const remoteVideo = this.remoteVideoElement.nativeElement;
      remoteVideo.srcObject = null;
      remoteVideo.pause();
    }

    if (this.localVideoElement?.nativeElement) {
      const localVideo = this.localVideoElement.nativeElement;
      localVideo.srcObject = null;
      localVideo.pause();
    }

    // Reset track ID tracking
    this.lastLocalVideoTrackId = null;
    this.lastRemoteVideoTrackId = null;
  }

  /**
   * Toggle fullscreen mode
   */
  toggleFullscreen(): void {
    this.fullscreenToggle.emit();
  }

  /**
   * Toggle local video pinned state
   */
  toggleLocalVideoPin(): void {
    this.isLocalVideoPinned = !this.isLocalVideoPinned;
  }

  /**
   * Check if local video should be visible
   * Only show if call has local video (hasVideo) and either pinned or controls visible
   */
  get isLocalVideoVisible(): boolean {
    // Don't show local video if not sending video (e.g., audio-only mode after declining video upgrade)
    if (!this.call?.hasVideo) {
      return false;
    }
    return this.isLocalVideoPinned || this.controlsVisible;
  }

  /**
   * Check if in receive-only mode (receiving remote video but not sending local video)
   */
  get isReceiveOnlyMode(): boolean {
    return !this.call?.hasVideo && !!this.call?.hasRemoteVideo;
  }

  /**
   * Request undock to separate window
   */
  requestUndock(): void {
    this.undockRequest.emit();
  }

  /**
   * Close video (downgrade to audio-only)
   */
  requestCloseVideo(): void {
    this.closeVideo.emit();
  }

  /**
   * Get CSS class for connection quality indicator
   */
  getConnectionQualityClass(): string {
    return `quality-${this.connectionQuality}`;
  }

  /**
   * Get tooltip text for connection quality with detailed RTP statistics
   */
  getConnectionQualityTooltip(): string {
    const qualityLabel = this.translate.instant(
      "ASTIMAX.PHONE.WIDGET.VIDEO.CONNECTION.QUALITY",
    );
    const qualityMessages: Record<ConnectionQuality, string> = {
      excellent: this.translate.instant(
        "ASTIMAX.PHONE.WIDGET.VIDEO.CONNECTION.EXCELLENT",
      ),
      good: this.translate.instant(
        "ASTIMAX.PHONE.WIDGET.VIDEO.CONNECTION.GOOD",
      ),
      fair: this.translate.instant(
        "ASTIMAX.PHONE.WIDGET.VIDEO.CONNECTION.FAIR",
      ),
      poor: this.translate.instant(
        "ASTIMAX.PHONE.WIDGET.VIDEO.CONNECTION.POOR",
      ),
    };

    const lines: string[] = [
      `${qualityLabel}: ${qualityMessages[this.connectionQuality]}`,
    ];

    if (this.currentVideoStats) {
      const stats = this.currentVideoStats;
      const inLabel = this.translate.instant(
        "ASTIMAX.PHONE.WIDGET.VIDEO.STATS.INBOUND",
      );
      const outLabel = this.translate.instant(
        "ASTIMAX.PHONE.WIDGET.VIDEO.STATS.OUTBOUND",
      );

      const packetsLostLabel = this.translate.instant(
        "ASTIMAX.PHONE.WIDGET.VIDEO.STATS.PACKETS_LOST",
      );
      const jitterLabel = this.translate.instant(
        "ASTIMAX.PHONE.WIDGET.VIDEO.STATS.JITTER",
      );

      // Outbound stats (what we send)
      if (stats.outbound) {
        const out = stats.outbound;
        const outBitrate = Math.round(out.bitrate / 1000);
        const codecs = [stats.outboundAudioCodec, out.codec]
          .filter(Boolean)
          .join("/");
        const jitter =
          out.jitter !== undefined && out.jitter > 0
            ? `${out.jitter.toFixed(1)} ms`
            : "n/a";
        lines.push(`${outLabel}:`);
        lines.push(
          `- ${out.resolution || "-"} @ ${out.fps || 0} FPS, ${outBitrate} kbps${codecs ? ", " + codecs : ""}`,
        );
        lines.push(
          `- ${packetsLostLabel}: ${out.packetsLost}, ${jitterLabel}: ${jitter}`,
        );
      }

      // Inbound stats (what we receive)
      if (stats.inbound) {
        const inb = stats.inbound;
        const inBitrate = Math.round(inb.bitrate / 1000);
        const codecs = [stats.inboundAudioCodec, inb.codec]
          .filter(Boolean)
          .join("/");
        const jitter =
          inb.jitter !== undefined && inb.jitter > 0
            ? `${inb.jitter.toFixed(1)} ms`
            : "n/a";
        lines.push(`${inLabel}:`);
        lines.push(
          `- ${inb.resolution || "-"} @ ${inb.fps || 0} FPS, ${inBitrate} kbps${codecs ? ", " + codecs : ""}`,
        );
        lines.push(
          `- ${packetsLostLabel}: ${inb.packetsLost}, ${jitterLabel}: ${jitter}`,
        );
      }
    }

    return lines.join("\n");
  }

  /**
   * Check if remote video is paused/muted
   */
  get isRemoteVideoPaused(): boolean {
    if (!this.call?.remoteStream) return true;
    const videoTracks = this.call.remoteStream.getVideoTracks();
    return videoTracks.length === 0 || !videoTracks[0].enabled;
  }

  /**
   * Check if local video is paused/muted
   */
  get isLocalVideoPaused(): boolean {
    return this.call?.isVideoMuted || false;
  }

  /**
   * Update component when call input changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["call"] && this.call) {
      // Update streams if video elements are ready
      if (this.remoteVideoElement && this.localVideoElement) {
        this.setupVideoStreams();
      }
    }

    if (changes["dtmfKey"] && this.dtmfKey) {
      this.dtmfDisplayKey = this.dtmfKey.key;
      // Toggle off then on to restart CSS animation even for same key
      this.showDtmfOverlay = false;
      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        this.showDtmfOverlay = true;
        this.cdr.detectChanges();
      });
    }
  }

  onDtmfAnimationEnd(): void {
    this.showDtmfOverlay = false;
  }

  onHiddenDtmfInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    input.value = "";

    for (const char of value) {
      if (VideoDisplayComponent.VALID_DTMF.has(char)) {
        this.dtmfInput.emit(char);
      }
    }
  }

  focusDtmfInput(): void {
    this.dtmfHiddenInput?.nativeElement.focus();
  }
}
